import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Building2, Plus, Loader2 } from 'lucide-react';
import { Container } from '../../components/primitives/Container';
import { LoadingScreen } from '../../components/primitives/LoadingScreen';
import { EmptyState } from '../../components/primitives/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import type { Company } from '../../types/database';

interface CompanySelectorProps {
  companies: Company[];
  loading: boolean;
  onSelect: (company: Company) => void;
  onCreateCompany: (company: Omit<Company, 'id' | 'created_at' | 'updated_at' | 'user_id'>) => Promise<void>;
}

export function CompanySelector({
  companies,
  loading,
  onSelect,
  onCreateCompany,
}: CompanySelectorProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    entity_type: 'business' as 'business' | 'personal' | 'individual',
    description: '',
    industry: '',
    tax_id: '',
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('O nome é obrigatório');
      return;
    }

    setCreating(true);
    try {
      await onCreateCompany(formData);
      toast.success('Empresa criada com sucesso!');
      setIsDialogOpen(false);
      setFormData({
        name: '',
        entity_type: 'business',
        description: '',
        industry: '',
        tax_id: '',
      });
    } catch (error: any) {
      toast.error(error.message || 'Erro ao criar empresa');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Carregando empresas..." />;
  }

  return (
    <Container className="py-12">
      <div className="mb-8 text-center">
        <h1>Selecione uma Empresa</h1>
        <p className="text-muted-foreground">
          Escolha a empresa que deseja gerenciar ou crie uma nova
        </p>
      </div>

      {companies.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="Nenhuma empresa encontrada"
          description="Comece criando sua primeira empresa para gerenciar suas finanças."
          action={
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Primeira Empresa
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            {companies.map((company) => (
              <Card
                key={company.id}
                variant="interactive"
                onClick={() => onSelect(company)}
              >
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="truncate">{company.name}</CardTitle>
                      <CardDescription className="capitalize">
                        {company.entity_type === 'business' && 'Empresa'}
                        {company.entity_type === 'personal' && 'Pessoal'}
                        {company.entity_type === 'individual' && 'Individual'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                {company.description && (
                  <CardContent>
                    <p className="line-clamp-2 text-sm text-muted-foreground">
                      {company.description}
                    </p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button onClick={() => setIsDialogOpen(true)} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Criar Nova Empresa
            </Button>
          </div>
        </>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Criar Nova Empresa</DialogTitle>
              <DialogDescription>
                Preencha os dados da empresa que deseja gerenciar
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome da empresa"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="entity_type">Tipo de Entidade</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, entity_type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="business">Empresa</SelectItem>
                    <SelectItem value="personal">Pessoal</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Breve descrição"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Setor</Label>
                <Input
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="Ex: Tecnologia, Comércio..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_id">CNPJ/CPF</Label>
                <Input
                  id="tax_id"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  placeholder="00.000.000/0000-00"
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={creating}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Criar Empresa
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
